import { CommonModule, JsonPipe } from '@angular/common';
import { AfterViewInit, OnDestroy, Component, computed, EventEmitter, inject, Output, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { LucideAngularModule } from 'lucide-angular';
import { ChatStore, ChatView } from 'src/app/stores/chat/chat.store';
import { UserStore } from 'src/app/stores/user.store';
import { CustomAttachmentListContext, CustomTemplatesService, StreamAutocompleteTextareaModule, StreamChatModule, StreamI18nService, ThemeService } from 'stream-chat-angular';
import { ChatHeaderComponent } from "../chat-header/chat-header.component";
import { OndemandUtilsService } from 'src/app/services/ondemand.utils/ondemand.utils.service';
import { AiStatusComponent } from "../ai-status/ai-status.component";
import { IncomingCallMessageComponent } from '../incoming-call-message/incoming-call-message.component';
import { EndedCallMessageComponent } from '../ended-call-message/ended-call-message.component';
import { InprogressCallMessageComponent } from '../inprogress-call-message/inprogress-call-message.component';
import { MissedCallMessageComponent } from '../missed-call-message/missed-call-message.component';
import { DisruptedCallMessageComponent } from "../disrupted-call-message/disrupted-call-message.component";
import { ProductMessageComponent } from '../product-message/product-message.component';
import { TimezoneService } from 'src/shared/services/timezone/timezone.service';
import { ChatDataService } from '../../../services/chat.data.service/chat.data.service';
import { interval, Subscription } from 'rxjs';
import { consultationexternal } from '../../../../shared/services/client/client';
import { MixpanelService } from '../../../../shared/services/mix-panel.service/mix-panel.service';
import { BookingDataService } from '../../../services/booking.data.service/booking.data.service';
import { NgIcon } from '@ng-icons/core';

@Component({
  selector: 'app-chat-window',
  standalone: true,
  imports: [StreamChatModule, TranslateModule, StreamAutocompleteTextareaModule, LucideAngularModule, ChatHeaderComponent, CommonModule, IncomingCallMessageComponent, EndedCallMessageComponent, InprogressCallMessageComponent, MissedCallMessageComponent, JsonPipe, DisruptedCallMessageComponent, AiStatusComponent, ProductMessageComponent, NgIcon],
  templateUrl: './chat-window.component.html',
  styleUrl: './chat-window.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ChatWindowComponent implements AfterViewInit, OnDestroy {
  // Services
  chatStore = inject(ChatStore);
  userStore = inject(UserStore);
  ondemandUtils = inject(OndemandUtilsService);
  themeService = inject(ThemeService);
  customTemplatesService = inject(CustomTemplatesService);
  streamI18nService = inject(StreamI18nService);
  timezoneService = inject(TimezoneService);
  chatAvailability = computed(() => this.ondemandUtils.getChatAvailabilityStatus(this.chatStore.availability(), this.chatStore.incompleteChats()));
  createChatDisabled = computed(() => this.chatStore.incompleteChats().length > 0 || this.chatStore.loadingChats() || this.chatStore.creatingChat() || this.chatAvailability() === 'unavailable');
  chatViewEnum = ChatView;
  @ViewChild("customAttachmentPreviews")
  customAttachmentPreviewsTemplate!: TemplateRef<CustomAttachmentListContext>;
  currentChatId = computed(() => { return (this.chatStore.activeChannel()?.data!['ExtraData'] as any)['chat_doc_id'] as string ?? '' });
  @Output() close = new EventEmitter<void>();
  subscriptions: Subscription = new Subscription()
  recommendedProductRepeatSubscription: Subscription = new Subscription();
  recommendedProductsRepeatInterval = interval(10000);
  recommendedProductsRepeatLimitCount: number = 4;
  recommendedProductsRepeatCount: number = 0;
  recommendedProductsResponse!: consultationexternal.InstantRecommendationResponse;
  recommendedProductsLastChatID!: string;
  showProductsOverlay: boolean = false;

  /**
   * Constructor
   * Initializes Stream I18n Service and sets the theme to light.
   */
  constructor(private chatDataService: ChatDataService,
              private mixpanelService: MixpanelService,
              private bookingDataService: BookingDataService) {
    this.streamI18nService.setTranslation();
    this.themeService.theme$.next('light');
  }
  ngAfterViewInit(): void {
    this.customTemplatesService.customAttachmentListTemplate$.next(this.customAttachmentPreviewsTemplate,)
    this.subscriptions.add(this.chatDataService.recommendedProductsObservable.subscribe(chatData => {
      this.recommendedProductsRepeatCount = 0;
      this.recommendedProductsLastChatID = chatData;
      this.getRecommendedProducts();
      this.startRecommendedProductPoll();
    }));
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    this.recommendedProductRepeatSubscription?.unsubscribe();
  }

  startRecommendedProductPoll(): void {
    this.recommendedProductRepeatSubscription = this.recommendedProductsRepeatInterval.subscribe(() => {
      this.getRecommendedProducts();
    });
  }

  getRecommendedProducts() {
    if (!this.recommendedProductsResponse && (this.recommendedProductsRepeatCount <= this.recommendedProductsRepeatLimitCount)) {
      const productRecommendationRequest = {
        ConsultationDocID: '',
        AppointmentDocID: '',
        ChatDocID: ''
      };
      if (this.recommendedProductsLastChatID) {
        productRecommendationRequest.ChatDocID = this.recommendedProductsLastChatID;
      }
      this.subscriptions.add(this.bookingDataService.GetRecommendedProductsByConsultation(productRecommendationRequest).subscribe({
        next: (result) => {
          this.handleRecommendedProductLimitBump();
          this.recommendedProductsResponse = result;
          this.showProductsOverlay = true;
          this.mixpanelService.track('product.instant_recommendations', {page_name: 'chat_product_instant_recommendations', chat_id: this.recommendedProductsLastChatID});
        },
        error: (error) => {
          this.handleRecommendedProductLimitBump();
        }
      }));
    }
  }

  handleRecommendedProductLimitBump() {
    this.recommendedProductsRepeatCount++;
    if (this.recommendedProductsRepeatCount >= this.recommendedProductsRepeatLimitCount) {
      this.recommendedProductRepeatSubscription.unsubscribe();
    }
  }
  /**
   * Creates a new chat with the active pet.
   */
  createNewChat() {
    const params = {
      pet_doc_id: this.userStore.activePet()?.doc_id || '',
      timezone: this.timezoneService.getClientTimezone(),
    }
    this.chatStore.startNewChat(params);
  }

  closeProductOverlay(): void {
    this.showProductsOverlay = false;
  }

  goToBasket(basketURL: string) {
    if (basketURL) {
      this.mixpanelService.track('product.checkout', {page_name: 'chat_product_instant_recommendations', chat_id: this.recommendedProductsLastChatID});
      window.open(basketURL, '_blank');
    }
  }

  goToCart(cartURL: string | undefined) {
    if (cartURL) {
      this.mixpanelService.track('product.basket', {page_name: 'chat_product_instant_recommendations', chat_id: this.recommendedProductsLastChatID});
      window.open(cartURL, '_blank');
    }
  }
}
